import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, Loader } from '@mantine/core';
import axios from 'axios';

import { getFreightFulfillmentPlanHandlingUnitPresets } from '../../../../api/ltl.api';

import type { FreightFulfillmentPlanHandlingUnitPreset } from '../../../../types/freight-responses';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

// Separator that won't appear in a saved handling unit preset's name.
const ITEM_VALUE_SEPARATOR = '|||';

const buildItemValue = (preset: FreightFulfillmentPlanHandlingUnitPreset): string =>
  `${preset.name}${ITEM_VALUE_SEPARATOR}${preset.handlingUnitPresetId}`;

const extractLabel = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[0];

const extractPresetId = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[1] ?? '';

export type HandlingUnitPresetAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  /** Called with the full record when a saved handling unit preset is picked. */
  onHandlingUnitPresetSelect: (preset: FreightFulfillmentPlanHandlingUnitPreset) => void;
  label: string;
  placeholder: string;
  'data-testid'?: string;
};

/**
 * A self-fetching Handling Unit Preset Name search field built on Mantine's `Autocomplete`,
 * searching seller-saved presets from `/freight/handling-unit-preset`.
 *
 * Mirrors CommodityProductAutocomplete — AbortController + 500 ms debounce,
 * MIN 3 chars, `name|||id` encoded values.
 */
export const HandlingUnitPresetAutocomplete = ({
  value,
  onChange,
  onHandlingUnitPresetSelect,
  label,
  placeholder,
  'data-testid': dataTestId,
}: HandlingUnitPresetAutocompleteProps) => {
  const [presets, setPresets] = useState<FreightFulfillmentPlanHandlingUnitPreset[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const didJustSubmitRef = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  const presetById = useMemo(() => new Map(presets.map((preset) => [String(preset.handlingUnitPresetId), preset])), [presets]);
  const data = useMemo(() => presets.map(buildItemValue), [presets]);

  const runSearch = useCallback((searchString: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsSearching(true);
    getFreightFulfillmentPlanHandlingUnitPresets(searchString, controller.signal)
      .then((results) => {
        if (!controller.signal.aborted) setPresets(results);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setPresets([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsSearching(false);
      });
  }, []);

  const handleChange = useCallback(
    (nextValue: string) => {
      if (didJustSubmitRef.current) {
        didJustSubmitRef.current = false;
        return;
      }

      onChange(nextValue);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (nextValue.length < MIN_SEARCH_LENGTH) {
        abortControllerRef.current?.abort();
        setIsSearching(false);
        setPresets([]);
        return;
      }

      debounceTimerRef.current = setTimeout(() => runSearch(nextValue), SEARCH_DEBOUNCE_MS);
    },
    [onChange, runSearch],
  );

  const handleOptionSubmit = useCallback(
    (itemValue: string) => {
      const preset = presetById.get(extractPresetId(itemValue));
      if (preset) {
        didJustSubmitRef.current = true;
        onChange(preset.name);
        onHandlingUnitPresetSelect(preset);
      }
    },
    [presetById, onChange, onHandlingUnitPresetSelect],
  );

  return (
    <Autocomplete
      data={data}
      data-testid={dataTestId}
      filter={({ options }) => options}
      label={label}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      placeholder={placeholder}
      renderOption={({ option }) => extractLabel(option.value)}
      rightSection={isSearching ? <Loader size='xs' /> : <FontAwesomeIcon icon={faMagnifyingGlass} size='sm' />}
      value={value}
    />
  );
};
